
import React, { useState, useEffect } from "react";
import { Tables } from "@/integrations/supabase/types";
import GoogleCrimeMap from "../maps/GoogleCrimeMap";
import FallbackLeafletMap from "../maps/FallbackLeafletMap";
import { useMapInit } from "@/hooks/maps/useMapInit";

type CrimeReport = Tables<"crime_reports">;

interface CrimeMapProps {
  crimeReports: CrimeReport[];
  showLast24Hours?: boolean;
  initialLocation?: { lat: number; lng: number } | null;
}

const CrimeMap: React.FC<CrimeMapProps> = ({ 
  crimeReports, 
  showLast24Hours = false,
  initialLocation = null 
}) => {
  const [useFallbackMap, setUseFallbackMap] = useState(false);
  const { isGoogleMapsLoaded, mapError } = useMapInit();

  // Calculate date 24 hours ago
  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  
  // Function to check if report is recent
  const isReportRecent = (reportDate: string) => new Date(reportDate) > twentyFourHoursAgo;

  // Auto switch to fallback map if Google Maps fails to load after a timeout
  useEffect(() => {
    if (mapError) {
      console.log("Google Maps error detected, switching to fallback map:", mapError);
      setUseFallbackMap(true);
    }
  }, [mapError]);

  return (
    <div className="relative w-full h-full">
      {useFallbackMap ? (
        <FallbackLeafletMap 
          crimeReports={showLast24Hours 
            ? crimeReports.filter(r => isReportRecent(r.created_at)) 
            : crimeReports
          } 
          isRecent={isReportRecent}
          initialLocation={initialLocation} 
        />
      ) : (
        <GoogleCrimeMap
          crimeReports={crimeReports}
          showLast24Hours={showLast24Hours}
          initialLocation={initialLocation}
          isGoogleMapsLoaded={isGoogleMapsLoaded}
          mapError={mapError}
          onUseFallbackMap={() => setUseFallbackMap(true)}
        />
      )}
    </div>
  );
};

export default CrimeMap;
